import React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, type TooltipProps } from 'recharts';
import type { VolumePoint } from '../../types/voip';
const COLORS = {
  outbound: '#00364d',
  inbound: '#7fa3b9',
  missed: '#dc2626',
  grid: '#eef2f6',
  axis: '#8c99a7'
};
const legend = [{
  label: 'Outbound',
  color: COLORS.outbound
}, {
  label: 'Inbound answered',
  color: COLORS.inbound
}, {
  label: 'Missed',
  color: COLORS.missed
}];
function ChartTooltip({
  active,
  payload,
  label
}: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
  return <div className="min-w-[160px] rounded-xl border border-line bg-surface p-3 text-xs shadow-lift" data-id="element-450" data-mp="3r0y8oyv6vqf8">
      <p className="mb-2 font-semibold text-ink" data-id="element-451" data-mp="9sb5i1j30daa">{label}</p>
      <ul className="space-y-1" data-id="element-452" data-mp="9st4v711j7mf">
        {[...payload].reverse().map(p => <li key={p.name} className="flex items-center justify-between gap-4" data-id="element-453" data-mp="4v1b07gu5bfl">
            <span className="flex items-center gap-1.5 text-muted" data-id="element-454" data-mp="2xeb1lxyqq1yl">
              <span className="h-2 w-2 rounded-sm" style={{
            backgroundColor: p.color
          }} aria-hidden data-id="element-455" data-mp="32c2qsk8039z3" />
              {p.name}
            </span>
            <span className="font-semibold text-ink" data-id="element-456" data-mp="36d68lc9lzg2y">{p.value}</span>
          </li>)}
      </ul>
      <p className="mt-2 flex justify-between border-t border-line pt-2 font-semibold text-ink" data-id="element-457" data-mp="3bels2zw51ej8">
        <span data-id="element-458" data-mp="3gblyxd0talbe">Total</span>
        <span data-id="element-459" data-mp="3ga13ll6oaych">{total}</span>
      </p>
    </div>;
}
interface CallVolumeChartProps {
  data: VolumePoint[];
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}
export function CallVolumeChart({
  data,
  title,
  subtitle,
  action
}: CallVolumeChartProps) {
  return <section aria-label={title} className="flex flex-col rounded-2xl border border-line bg-surface p-6 shadow-card" data-id="element-460" data-mp="3u30etazfa6e8">
      {action && <div className="mb-4 flex flex-wrap items-start justify-between gap-3" data-id="element-461" data-mp="7wbrdpe5knw8">
          <div data-id="element-462" data-mp="7tz9tdcb4fjp">
            <h2 className="text-base font-semibold text-ink" data-id="element-463" data-mp="23zghn1pj0y4o">{title}</h2>
            {subtitle && <p className="mt-0.5 text-sm text-muted" data-id="element-464" data-mp="23x3xchwaqbog">{subtitle}</p>}
          </div>
          {action}
        </div>}
      <div className={`mb-5 flex flex-wrap items-center gap-3 ${action ? 'justify-start' : 'justify-between'}`} data-id="element-465" data-mp="2dp022bsw101n">
        {!action && <h2 className="text-base font-semibold text-ink" data-id="element-466" data-mp="28xi7c5hgjvp4">{title}</h2>}
        <ul className="flex flex-wrap items-center gap-4" data-id="element-467" data-mp="2mpoej22muscf">
          {legend.map(l => <li key={l.label} className="flex items-center gap-1.5 text-xs text-muted" data-id="element-468" data-mp="2iomnv9ms7om2">
              <span className="h-2.5 w-2.5 rounded-sm" style={{
            backgroundColor: l.color
          }} aria-hidden data-id="element-469" data-mp="2wo4593xqzzle" />
              {l.label}
            </li>)}
        </ul>
      </div>
      <div className="h-72 w-full" data-id="element-470" data-mp="2rmyaz1ofw49r">
        <ResponsiveContainer width="100%" height="100%" data-id="element-471" data-mp="129l2d09anu2u">
          <BarChart data={data} margin={{
          top: 4,
          right: 0,
          left: -18,
          bottom: 0
        }} barCategoryGap={data.length > 12 ? '18%' : '38%'} data-id="element-472" data-mp="12c79iys9w7kj">
            <CartesianGrid vertical={false} stroke={COLORS.grid} data-id="element-473" data-mp="38yn51zzmn5ne" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{
            fontSize: 12,
            fill: COLORS.axis
          }} interval="preserveStartEnd" minTickGap={14} dy={6} data-id="element-474" data-mp="390gajbyi7jtt" />
            <YAxis tickLine={false} axisLine={false} tick={{
            fontSize: 12,
            fill: COLORS.axis
          }} data-id="element-475" data-mp="2z7sfomr33pxq" />
            <Tooltip content={<ChartTooltip data-id="element-477" data-mp="34628wrt90ggp" />} cursor={{
            fill: 'rgba(0,54,77,0.04)'
          }} data-id="element-476" data-mp="346c3ksp8nbsq" />
            <Bar dataKey="outbound" name="Outbound" stackId="calls" fill={COLORS.outbound} animationDuration={300} animationEasing="ease-out" data-id="element-478" data-mp="2q6ua400zzad1" />
            <Bar dataKey="inbound" name="Inbound answered" stackId="calls" fill={COLORS.inbound} animationDuration={300} animationEasing="ease-out" data-id="element-479" data-mp="2v4vssjuih7nz" />
            <Bar dataKey="missed" name="Missed" stackId="calls" fill={COLORS.missed} radius={[4, 4, 0, 0]} animationDuration={300} animationEasing="ease-out" data-id="element-480" data-mp="2l7zjibwsghid" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>;
}